import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/#services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/jobs", label: "Jobs" },
  { to: "/resume-review", label: "AI Review" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") { navigate("/"); setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300); }
      else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else navigate(to);
  };

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40 shadow-[0_1px_30px_rgba(10,17,40,0.06)]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className="w-9 h-9 bg-navy text-white grid place-items-center font-display text-xl leading-none">J</span>
          <span className="font-display text-lg font-bold tracking-tight text-navy">Joy<span className="text-cobalt">.</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button key={l.label} data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`} onClick={() => go(l.to)}
              className="text-sm font-medium text-navy/80 hover:text-cobalt transition-colors">
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Link to="/dashboard" data-testid="nav-dashboard" className="text-sm font-medium text-navy hover:text-cobalt transition-colors">Dashboard</Link>
              <button data-testid="nav-logout" onClick={async () => { await logout(); navigate("/"); }}
                className="text-sm font-medium px-5 py-2.5 rounded-full bg-navy text-white hover:bg-cobalt transition-colors">Log out</button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-navy hover:text-cobalt transition-colors">Sign in</Link>
              <button data-testid="nav-cta" onClick={() => go("/#contact")}
                className="text-sm font-medium px-5 py-2.5 rounded-full bg-navy text-white hover:bg-cobalt transition-colors">Get started</button>
            </>
          )}
        </div>

        <button data-testid="nav-mobile-toggle" className="md:hidden text-navy" onClick={() => setOpen(!open)}>
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-b border-border px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <button key={l.label} onClick={() => go(l.to)} className="text-left text-navy font-medium">{l.label}</button>
          ))}
          <div className="h-px bg-border" />
          {user ? (
            <>
              <button onClick={() => go("/dashboard")} className="text-left text-navy font-medium">Dashboard</button>
              <button onClick={async () => { await logout(); navigate("/"); }} className="text-left text-cobalt font-medium">Log out</button>
            </>
          ) : (
            <>
              <button onClick={() => go("/login")} className="text-left text-navy font-medium">Sign in</button>
              <button onClick={() => go("/#contact")} className="text-left text-cobalt font-medium">Get started</button>
            </>
          )}
        </div>
      )}
    </header>
  );
}
